var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef } from 'react';
import cx from 'classnames';
import { MediaQueries } from '@move-ui/theme';
import { useMediaQuery } from '@move-web-essentials-hooks/use-media-query';
import { omit } from 'lodash';
import { NativeDropdown } from './NativeDropdown/NativeDropdown';
import { flatMap } from './utils/arrayFunctions';
import { Option } from './Option/Option';
import { MobileMenu } from './MobileMenu/MobileMenu';
import { DesktopMenu } from './DesktopMenu/DesktopMenu';
import styles from './Dropdown.module.css';
import { useKeyDown } from './hooks/useKeyDown';
import { stopEvent } from './utils/stopEvent';
export const Dropdown = forwardRef((props, parentRef) => {
    var _a;
    const { sections, label, modalLabel, required, placeholder, options, className, 'data-testid': testId, onOpen, onClose, onModalClose, placeholderMeta, minMenuWidth, multiple, onChange, onBlur, appliedValues = [], withSearch, noResultsComponent, searchProps: searchParams = {} } = props, rest = __rest(props, ["sections", "label", "modalLabel", "required", "placeholder", "options", "className", 'data-testid', "onOpen", "onClose", "onModalClose", "placeholderMeta", "minMenuWidth", "multiple", "onChange", "onBlur", "appliedValues", "withSearch", "noResultsComponent", "searchProps"]);
    const { value = multiple ? undefined : '' } = rest;
    const { onApply, resetComponent: ResetComponent, applyComponent: ApplyComponent } = props;
    const [open, setOpen] = useState(false);
    const [scrollTopKey, setScrollTopKey] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const ref = useRef(null);
    const isDesktop = useMediaQuery(MediaQueries.MEDIUM);
    useEffect(() => {
        setSearchValue('');
    }, [open]);
    const handleOpen = useCallback(() => {
        setOpen(true);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    }, [setOpen, onOpen]);
    const handleClose = useCallback(() => {
        var _a;
        setOpen(false);
        onClose === null || onClose === void 0 ? void 0 : onClose();
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [setOpen, onClose]);
    const handleCloseWithApply = useCallback(() => {
        onApply === null || onApply === void 0 ? void 0 : onApply(value);
        handleClose();
    }, [handleClose, onApply, value]);
    const handleModalClose = useCallback(() => {
        setOpen(false);
        onModalClose === null || onModalClose === void 0 ? void 0 : onModalClose();
        if (multiple) {
            onChange(appliedValues);
        }
    }, [setOpen, onModalClose, multiple, onChange, appliedValues]);
    useEffect(() => {
        if (sections && options) {
            // eslint-disable-next-line no-console
            console.error(`Dropdown ${rest.id}: options and sections params can't be used together. Sections will take priority over options. Please remove options prop.`);
        }
    }, [sections, options, rest.id]);
    const optionsWithoutSections = useMemo(() => (sections === null || sections === void 0 ? void 0 : sections.length)
        ? flatMap(sections, ({ values }, index) => values.map((option) => (Object.assign(Object.assign({}, option), { sectionIndex: index }))))
        : options || [], [options, sections]);
    const optionArray = useMemo(() => [
        ...(required || multiple
            ? []
            : [
                { label: placeholder, description: placeholderMeta }
            ]),
        ...optionsWithoutSections
    ], [required, multiple, placeholder, placeholderMeta, optionsWithoutSections]);
    // This is used to avoid unique ids on the Checkbox inputs when the same Dropdown can be on multiple components on screen
    const uniquePrefix = useMemo(() => Math.random(), []);
    const optionList = useMemo(() => optionArray === null || optionArray === void 0 ? void 0 : optionArray.filter((option) => {
        var _a;
        return searchValue.length < 1 ||
            ((_a = option.label) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase().startsWith(searchValue.toLocaleLowerCase()));
    }).filter((option, pos, self) => searchValue.length < 1 ||
        self.findIndex((opt) => opt.value === option.value) === pos).map((option) => {
        var _a, _b, _c;
        const section = option.sectionIndex === undefined
            ? undefined
            : sections === null || sections === void 0 ? void 0 : sections[option.sectionIndex];
        const isFirstElementOnSection = ((_a = section === null || section === void 0 ? void 0 : section.values[0]) === null || _a === void 0 ? void 0 : _a.value) === option.value;
        const id = `${uniquePrefix}${(section === null || section === void 0 ? void 0 : section.label) || ''}-${option.value || 'empty'}`;
        return (React.createElement(Option, Object.assign({ key: id, id: id }, option, { multiple: multiple, onClick: () => {
                if (!multiple) {
                    handleClose();
                    onChange(option.value);
                    return;
                }
                const values = value || [];
                const newValues = values.includes(option.value)
                    ? values.filter((val) => val !== option.value)
                    : [...values, option.value];
                onChange(newValues);
            }, selected: (_b = (multiple ? value : [value])) === null || _b === void 0 ? void 0 : _b.includes((_c = option.value) !== null && _c !== void 0 ? _c : ''), sectionLabel: isFirstElementOnSection && searchValue.length < 1
                ? section === null || section === void 0 ? void 0 : section.label
                : undefined, icon: option.icon })));
    }), [
        sections,
        optionArray,
        handleClose,
        onChange,
        value,
        multiple,
        uniquePrefix,
        searchValue
    ]);
    const onClick = useCallback((event) => {
        handleOpen();
        stopEvent(event);
    }, [handleOpen]);
    useKeyDown('Escape', handleCloseWithApply);
    const onKeyDown = useCallback((event) => {
        // eslint-disable-next-line default-case
        switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown':
            case 'Enter':
            case ' ':
                if (open) {
                    handleClose();
                }
                else {
                    handleOpen();
                }
                stopEvent(event);
                break;
        }
    }, [open, handleOpen, handleClose]);
    const handleChange = useCallback(({ target }) => {
        const values = Object.values(target.selectedOptions).map((option) => option.value);
        if (multiple) {
            onChange(values);
            return;
        }
        onChange(values[0]);
    }, [onChange, multiple]);
    const searchInputRef = useRef(null);
    const [searchFocused, setSearchFocused] = useState(false);
    const handleClearSearch = useCallback(() => {
        var _a;
        setSearchValue('');
        (_a = searchParams === null || searchParams === void 0 ? void 0 : searchParams.onClear) === null || _a === void 0 ? void 0 : _a.call(searchParams, searchValue);
        setScrollTopKey(`${Math.random()}`);
    }, [searchParams, searchValue]);
    const searchProps = withSearch
        ? Object.assign(Object.assign(Object.assign({}, searchParams), { value: searchValue, setValue: setSearchValue, onFocus: () => setSearchFocused(true), onBlur: () => setSearchFocused(false), inputRef: searchInputRef, onClear: handleClearSearch }), (open ? {} : { tabIndex: -1 })) : undefined;
    const handleReset = useCallback(() => {
        handleClearSearch();
        onChange([]);
    }, [handleClearSearch, onChange]);
    const handleBlur = useCallback((event) => {
        if (onBlur &&
            ref.current &&
            !ref.current.contains(event.relatedTarget)) {
            onBlur(event);
        }
    }, [onBlur]);
    const resetDisabled = searchValue.length < 1 && (!value || (value === null || value === void 0 ? void 0 : value.length) < 1);
    const footer = useMemo(() => multiple &&
        (ResetComponent || ApplyComponent) && (React.createElement("div", { className: cx(styles.footer, {
            [styles.sticky]: !withSearch || isDesktop
        }) },
        ResetComponent && (React.createElement(ResetComponent, Object.assign({ onClick: handleReset, disabled: resetDisabled }, (open ? {} : { tabIndex: -1 })))),
        ApplyComponent && (React.createElement(ApplyComponent, Object.assign({ onClick: handleCloseWithApply }, (open ? {} : { tabIndex: -1 })))))), [
        multiple,
        handleReset,
        handleCloseWithApply,
        ResetComponent,
        ApplyComponent,
        resetDisabled,
        open,
        withSearch,
        isDesktop
    ]);
    const selectedIcon = (_a = optionArray === null || optionArray === void 0 ? void 0 : optionArray.find((element) => element.value === value)) === null || _a === void 0 ? void 0 : _a.icon;
    const filteredOptions = useMemo(() => (searchValue.length > 0 && optionList.length < 1 ? [] : optionList), [optionList, searchValue]);
    return (React.createElement("div", { "data-testid": testId ? `${testId}Container` : undefined, className: cx(styles.container, { [styles.open]: open }), ref: ref, onBlur: handleBlur },
        React.createElement(NativeDropdown, Object.assign({}, omit(rest, ['resetComponent', 'applyComponent', 'onApply']), { "data-testid": testId, label: label, value: value, options: optionsWithoutSections, required: required, placeholder: placeholder, onChange: handleChange, onClick: onClick, onMouseDown: stopEvent, onKeyDown: onKeyDown, className: cx(className, styles.select, {
                [styles.open]: open,
                [styles.focused]: open && !searchFocused
            }), iconClassName: cx({ [styles.iconOpen]: open }), selectedIcon: selectedIcon, sections: sections, multiple: multiple, appliedValues: appliedValues, blockDoubleOpen: true, ref: parentRef })),
        !isDesktop && (React.createElement("div", { className: cx(styles.mobileMenuContainer, {
                [styles.closed]: !open
            }) },
            React.createElement("div", { "data-testid": testId ? `${testId}MobileMenuOverlay` : undefined, className: cx(styles.overlay, { [styles.closed]: !open }), onClick: handleModalClose, role: "presentation", tabIndex: -1 }),
            React.createElement(MobileMenu, { scrollTopKey: scrollTopKey, open: open, label: modalLabel || label, onClose: handleModalClose, "data-testid": testId, footer: footer, multiple: multiple, searchProps: searchProps, noResultsComponent: noResultsComponent }, filteredOptions))),
        isDesktop && (React.createElement(DesktopMenu, { scrollTopKey: scrollTopKey, open: open, minMenuWidth: minMenuWidth, hasSections: sections && (required || multiple), onClose: handleCloseWithApply, "data-testid": testId, footer: footer, multiple: multiple, searchProps: searchProps, noResultsComponent: noResultsComponent }, filteredOptions))));
});
//# sourceMappingURL=Dropdown.js.map